<template>
  <div v-if="isBackground" ref="bgRef" :style="bgStyle" @mouseenter="isHovered = true" @mouseleave="isHovered = false"
    v-bind="$attrs">
    <slot></slot>
  </div>
  <img v-else-if="!useWebp" :src="computedSrc" :alt="alt" :loading="loading" :decoding="decoding"
    :fetchpriority="fetchpriority" v-bind="$attrs" />
  <picture v-else v-bind="$attrs">
    <source v-if="computedWebpSrc" :srcset="computedWebpSrc" type="image/webp" />
    <img :src="computedSrc" :alt="alt" :loading="loading" :decoding="decoding" :fetchpriority="fetchpriority"
      :class="$attrs.class" :style="$attrs.style" />
  </picture>
</template>

<script>
import {
  getStaticImageUrl,
  checkWebpSupport,
  isWebpEnabled,
} from "@/utils/StaticPathUtil";

/**
 * 支援自動轉換為 WebP 的副檔名清單（共同控管常數）
 */
export const SUPPORTED_WEBP_EXTENSIONS = ["png", "jpg", "jpeg", "gif"];
const WEBP_EXT_REGEXP = new RegExp(
  `\\.(${SUPPORTED_WEBP_EXTENSIONS.join("|")})$`,
  "i"
);

/**
 * 將圖檔路徑轉換為對應的 WebP 路徑
 * - 若本身已是 .webp，直接回傳
 * - 若符合可轉換格式 (預設 png, jpg, jpeg, gif)，替換副檔名為 .webp
 * - 其餘格式回傳空字串
 * @param {string} path 圖片來源路徑
 * @param {string[]} [customExtensions] 自訂支援的副檔名清單 (選填)
 * @returns {string} WebP 圖片路徑
 */
export function toWebpPath(path, customExtensions) {
  if (typeof path !== "string" || !path) return "";
  if (/\.webp$/i.test(path)) {
    return path;
  }
  const regex =
    customExtensions && customExtensions !== SUPPORTED_WEBP_EXTENSIONS
      ? new RegExp(`\\.(${customExtensions.join("|")})$`, "i")
      : WEBP_EXT_REGEXP;
  if (regex.test(path)) {
    return path.replace(regex, ".webp");
  }
  return "";
}

/**
 * StaticImage 元件
 * 用於統一管理專案中所有圖片的加載。
 * 支持作為普通 <img> 標籤（使用 HTML5 <picture> 自動向下相容），或設置 isBackground 作為 CSS 背景圖容器。
 * 支援自動偵測瀏覽器 WebP 相容度：若瀏覽器支援 WebP，優先載入 .webp 圖檔，否則自動降級使用原始圖檔 (.png / .jpg / .gif)。
 * 設置 isBackground 時，可額外設置 hoverSrc 支援滑鼠懸停圖片切換效果。
 * 開發階段：使用專案內 static/assets 圖片
 * 正式 Release：若設定 VUE_APP_CLOUD_STORAGE_URL 或 VUE_APP_USE_CLOUD_STORAGE，自動切換使用 Cloud Storage
 * 提供原生的圖片懶加載 (loading="lazy") 與背景圖片進場（IntersectionObserver）懶加載能力。
 */
export default {
  name: "StaticImage",
  inheritAttrs: false,
  props: {
    /**
     * 圖片來源路徑或檔名 (必填)
     * - 支援傳入 static 圖檔檔名（如 'logo.png'），自動透過 StaticPathUtil 解析對應路徑
     * - 若設定 Cloud Storage 環境變數，將自動轉為雲端圖床 URL
     */
    src: {
      type: [String, Object],
      required: true,
    },
    /**
     * 滑鼠懸停 (hover) 時切換顯示的圖片來源
     * - 用於 isBackground 時的背景圖 hover 切換效果
     */
    hoverSrc: {
      type: [String, Object],
      default: "",
    },
    /**
     * 圖片替代文字 (alt 屬性，用於 a11y 無障礙與 SEO)
     */
    alt: {
      type: String,
      default: "",
    },
    /**
     * 是否作為 CSS 背景圖容器 (div) 渲染
     * - true: 渲染為 <div :style="bgStyle"><slot></slot></div>，可作為父層背景容器
     * - false: 渲染為一般 <img> 或 <picture> 標籤
     */
    isBackground: {
      type: Boolean,
      default: false,
    },
    /**
     * 原生 <img> 圖片載入模式 (原生 Lazy Loading)
     * - 'lazy': 進入可視範圍前才載入 (預設)
     * - 'eager': 立即載入 (適用於首屏重要圖檔/LCP 圖片)
     */
    loading: {
      type: String,
      default: "lazy",
    },
    /**
     * 原生 <img> 圖片解碼模式
     * - 'async': 非同步解碼，避免阻塞主執行緒 (預設)
     * - 'sync': 同步解碼
     * - 'auto': 瀏覽器自適應
     */
    decoding: {
      type: String,
      default: "async",
    },
    /**
     * 圖片資源優先級提示 (Resource Fetch Priority)
     * - 'auto': 預設由瀏覽器自行判斷
     * - 'high': 最高優先級，適用於首屏 Hero/LCP 主視覺圖檔
     * - 'low': 低優先級
     */
    fetchpriority: {
      type: String,
      default: "auto",
    },
    /**
     * 背景圖片是否啟用視口進場懶加載 (IntersectionObserver)
     * - 僅在 isBackground: true 時生效
     * - true: 元件滾動至視窗附近時才載入 background-image (預設)
     * - false: 立即載入背景圖
     */
    lazyBackground: {
      type: Boolean,
      default: true,
    },
    /**
     * 背景圖複合樣式模板 (僅在 isBackground: true 時生效)
     * - 例如: "linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url()"
     * - 元件會將其中的 url() 自動替換為真實圖片路徑 url(...)
     */
    backgroundStyle: {
      type: String,
      default: "",
    },
    /**
     * 是否自動嘗試載入 WebP 格式圖片
     * - true: 自動將 .png/.jpg/.gif 映射為 .webp 並透過 <picture> 或背景圖判斷載入
     * - false: 載入原始格式 (預設)
     */
    useWebp: {
      type: Boolean,
      default: false,
    },
    /**
     * 支援轉換為 WebP 的副檔名清單（參數控管，預設為 ['png', 'jpg', 'jpeg', 'gif']）
     */
    supportedExtensions: {
      type: Array,
      default: () => SUPPORTED_WEBP_EXTENSIONS,
    },
  },
  data() {
    return {
      isHovered: false,
      isIntersected: false,
      observer: null,
      supportsWebp: false,
    };
  },
  computed: {
    webpSrcPath() {
      if (!isWebpEnabled() || !this.useWebp || typeof this.src !== "string")
        return "";
      return this.toWebpPath(this.src);
    },
    webpHoverSrcPath() {
      if (
        !isWebpEnabled() ||
        !this.useWebp ||
        typeof this.hoverSrc !== "string"
      )
        return "";
      return this.toWebpPath(this.hoverSrc);
    },
    computedWebpSrc() {
      if (!this.webpSrcPath) return "";
      return getStaticImageUrl(this.webpSrcPath);
    },
    computedWebpHoverSrc() {
      if (!this.webpHoverSrcPath) return "";
      return getStaticImageUrl(this.webpHoverSrcPath);
    },
    computedSrc() {
      if (!this.src) return "";
      return getStaticImageUrl(this.src);
    },
    computedHoverSrc() {
      if (!this.hoverSrc) return "";
      return getStaticImageUrl(this.hoverSrc);
    },
    currentBgSrc() {
      const useHover = this.isHovered;
      if (this.useWebp && isWebpEnabled() && this.supportsWebp) {
        if (useHover && this.computedWebpHoverSrc) {
          return this.computedWebpHoverSrc;
        }
        if (this.computedWebpSrc) {
          return this.computedWebpSrc;
        }
      }
      if (useHover && this.computedHoverSrc) {
        return this.computedHoverSrc;
      }
      return this.computedSrc;
    },
    bgStyle() {
      if (this.lazyBackground && !this.isIntersected) {
        return {};
      }

      let style = "";
      if (this.backgroundStyle == "") {
        style = `url(${this.currentBgSrc})`;
      } else {
        style = this.backgroundStyle.replace(
          "url()",
          `url(${this.currentBgSrc})`
        );
      }
      return {
        backgroundImage: style,
      };
    },
  },
  methods: {
    /**
     * 取得 WebP 圖片路徑之方法
     * @param {string} path 圖片來源路徑
     * @returns {string} WebP 圖片路徑
     */
    toWebpPath(path) {
      return toWebpPath(path, this.supportedExtensions);
    },
    getWebpPath(path) {
      return this.toWebpPath(path);
    },
  },
  mounted() {
    this.supportsWebp = checkWebpSupport();

    if (this.isBackground && this.lazyBackground) {
      if (typeof window !== "undefined" && "IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0] && entries[0].isIntersecting) {
              this.isIntersected = true;
              if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
              }
            }
          },
          { rootMargin: "200px 0px" }
        );
        if (this.$refs.bgRef) {
          this.observer.observe(this.$refs.bgRef);
        }
      } else {
        this.isIntersected = true;
      }
    } else {
      this.isIntersected = true;
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
};
</script>
